import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { XCircle, CheckCircle } from 'lucide-react';
import { useTestSession, type ReviewItem } from '@/contexts/useTestSession';
import { useAuth } from '@/contexts/useAuth';
import ResultsHero from '@/components/interview/ResultsHero';
import Button from '@/components/ui/Button';

interface CategoryBreakdownProps {
  categories: Array<{
    category: string;
    total: number;
    correct: number;
    percentage: number;
  }>;
}

function CategoryBreakdown({ categories }: CategoryBreakdownProps) {
  return (
    <section>
      <h3 className="font-display font-semibold text-lg md:text-xl text-text-primary mb-5">
        Areas to improve
      </h3>
      <div className="flex flex-col gap-4">
        {categories.map((cat) => (
          <div key={cat.category}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-sans text-sm font-medium text-text-primary">
                {cat.category}
              </span>
              <span className="font-sans text-xs text-text-muted">
                {cat.correct}/{cat.total} ({cat.percentage}%)
              </span>
            </div>
            <div className="h-2 bg-border/30 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  cat.percentage >= 60 ? 'bg-success' : 'bg-warning'
                }`}
                style={{ width: `${cat.percentage}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

interface IncorrectAnswersListProps {
  items: ReviewItem[];
}

function IncorrectAnswersList({ items }: IncorrectAnswersListProps) {
  const incorrectItems = items.filter((item) => !item.isCorrect);
  if (incorrectItems.length === 0) return null;

  return (
    <section>
      <h3 className="font-display font-semibold text-lg md:text-xl text-text-primary mb-5">
        Review incorrect answers
      </h3>
      <div className="flex flex-col gap-5">
        {incorrectItems.map((item) => (
          <div
            key={item.questionId}
            className="border-b border-border/30 pb-5 last:border-0 last:pb-0"
          >
            <p className="font-sans text-sm font-medium text-text-primary mb-3">
              {item.questionText}
            </p>
            <div className="flex items-start gap-2 mb-2">
              <XCircle size={16} className="text-error flex-shrink-0 mt-0.5" />
              <p className="font-sans text-sm text-error">
                {item.userAnswer || 'Skipped'}
              </p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle size={16} className="text-success flex-shrink-0 mt-0.5" />
              <p className="font-sans text-sm text-success-dark">
                {item.correctAnswer}
              </p>
            </div>
            {item.explanation && (
              <p className="font-sans text-xs text-text-muted mt-2 pl-6">
                {item.explanation}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-cream/40 px-4 py-3 text-center">
      <p className="font-display font-semibold text-xl md:text-2xl text-text-primary">
        {value}
      </p>
      <p className="font-sans text-[11px] uppercase tracking-[0.15em] text-text-muted mt-1">
        {label}
      </p>
    </div>
  );
}

function formatTime(minutes: number | undefined): string {
  if (minutes === undefined) return '—';
  const m = Math.max(1, Math.round(minutes));
  return `${m} min`;
}

export default function ResultsPage() {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const { status, results, reviewItems, resetTest } = useTestSession();
  const isLeavingRef = useRef(false);

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  useEffect(() => {
    if (isLeavingRef.current) return;
    if (status !== 'completed' || !results) {
      navigate('/home', { replace: true });
    }
  }, [status, results, navigate]);

  if (!results) return null;

  const handleTryAgain = () => {
    isLeavingRef.current = true;
    resetTest();
    navigate('/interview/intro', { replace: true });
  };

  const handleBackHome = () => {
    isLeavingRef.current = true;
    resetTest();
    navigate('/home', { replace: true });
  };

  const hasCategories =
    results.weakCategories && results.weakCategories.length > 0;
  const hasReview = reviewItems.some((item) => !item.isCorrect);

  return (
    <div className="relative min-h-screen bg-cream md:bg-gradient-to-br md:from-cream md:via-cream md:to-cream-warm overflow-hidden flex flex-col">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[700px] hidden md:block"
        style={{
          background:
            'radial-gradient(ellipse 900px 500px at 20% 10%, rgba(212,163,115,0.18), transparent 60%)',
        }}
      />

      <main className="relative flex-1 flex items-center justify-center px-6 py-8 md:py-12 lg:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-3xl bg-card border border-border rounded-2xl shadow-sm p-6 md:p-10 lg:p-12"
        >
          <ResultsHero
            passed={results.passed}
            correct={results.correctAnswers}
            total={results.totalQuestions}
          />

          <div className="mt-8 grid grid-cols-3 gap-3 max-w-md mx-auto">
            <StatCard
              label="Score"
              value={`${results.correctAnswers}/${results.totalQuestions}`}
            />
            <StatCard label="Result" value={`${results.scorePercentage}%`} />
            <StatCard
              label="Time"
              value={formatTime(results.timeTakenMinutes)}
            />
          </div>

          {hasCategories && (
            <>
              <div className="h-px bg-border my-8 md:my-10" />
              <CategoryBreakdown categories={results.weakCategories!} />
            </>
          )}

          {hasReview && (
            <>
              <div className="h-px bg-border my-8 md:my-10" />
              <IncorrectAnswersList items={reviewItems} />
            </>
          )}

          <div className="h-px bg-border my-8 md:my-10" />

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Button variant="secondary" onClick={handleBackHome}>
              Back to home
            </Button>
            <Button onClick={handleTryAgain}>Try again</Button>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
